import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import React from "react";
import { TItem } from "../types/index";
import { Controller, useForm } from "react-hook-form";
import { classNames as cx } from "primereact/utils";
import { If } from "../../../../components/If/If";
import classes from "./SetItemPopup.module.scss";
import { Dropdown } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import { useEffect, useState } from "react";
import { LabelValue } from "../../../../components/LabelValue/LabelValue";

type SetItemPopupProps = {
  onHide: () => void;
  onSubmit: (item: {
    name: string;
    id: string | null;
    projectTypeId: string;
    contractTypeId: string;
    file: File;
    sowTemplateFile: File | null;
  }) => void;
  item: TItem | null;
  projectTypeOptions?: [{ label: string; value: string }];
  // The AirSlate DocGen template - contract type admin page only.
  showAirslateTemplate?: boolean;
  // The Excel scope-of-work template - shown wherever contract types are edited.
  showSowTemplate?: boolean;
};

type TForm = {
  name: string;
  projectTypeId?: string;
  contractTypeId?: string;
  file: File;
};

export const SetItemPopup = ({
  onHide,
  onSubmit,
  item,
  projectTypeOptions,
  showAirslateTemplate = false,
  showSowTemplate = false,
}: SetItemPopupProps) => {
  const [sowTemplateFile, setSowTemplateFile] = useState<File | null>(null);
  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<TForm>({
    defaultValues: {
      name: item?.name || "",
      projectTypeId: item?.projectTypeId || "",
      contractTypeId: item?.contractTypeId || "",
    },
    shouldFocusError: false,
  });

  const file = watch("file");

  const handleSave = (formData: TForm) => {
    onSubmit({
      name: formData.name,
      id: item?.id || null,
      projectTypeId: formData.projectTypeId || "",
      contractTypeId: formData.contractTypeId || "",
      //@ts-ignore
      file: formData.file?.[0],
      sowTemplateFile,
    });
  };

  useEffect(() => {
    if (item?.projectTypeId || item?.contractTypeId) {
      reset((prev) => ({
        ...prev,
        projectTypeId: item.projectTypeId || "",
        contractTypeId: item.contractTypeId || "",
      }));
    }
    setSowTemplateFile(null);
    return () => reset();
  }, [item?.contractTypeId, item?.projectTypeId, reset]);

  useEffect(() => {
    const toDataURL = async (url: string) => {
      const response = await fetch(url);
      const blob = await response.blob();
      const dataurl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      return dataurl;
    };

    function dataURLtoFile(dataurl: any, filename: string) {
      var arr = dataurl.split(","),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    }

    const convertUrlToFile = async (url: string, fileName: string) => {
      const dataUrl = await toDataURL(url);
      const file = dataURLtoFile(dataUrl, fileName);
      //@ts-ignore
      setValue("file", { 0: file, length: 1 });
    };

    if (item?.contractTypeTemplatePath) {
      const paths = item.contractTypeTemplatePath.split("/");
      convertUrlToFile(
        `${process.env.REACT_APP_DSC_SERVICE_DOMAIN}/${item.contractTypeTemplatePath}`,
        paths[paths.length - 1]
      );
    }
  }, [item?.contractTypeTemplatePath, setValue]);

  const productDialogFooter = (
    <React.Fragment>
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text p-button-secondary"
        onClick={onHide}
      />
      <Button
        label="Save"
        icon="pi pi-check"
        className="p-button-text p-button-primary"
        onClick={handleSubmit(handleSave)}
      />
    </React.Fragment>
  );

  const isScopeOfWorkItem =
    item?.id === "projectType" ||
    item?.id === "contractType" ||
    !!item?.projectTypeId ||
    !!item?.contractTypeId;

  return (
    <Dialog
      visible={true}
      style={{ width: "450px" }}
      header="Details"
      modal
      className="p-fluid"
      footer={productDialogFooter}
      onHide={onHide}
      draggable={false}
    >
      <If condition={isScopeOfWorkItem}>
        <div className={classes.field}>
          <LabelValue label="Project type" isRequired />
          <Controller
            name="projectTypeId"
            render={({ field }) => (
              <Dropdown
                {...field}
                value={item?.contractTypeId ? watch("contractTypeId") : field.value}
                appendTo="self"
                options={projectTypeOptions}
                ariaLabel="name"
                placeholder="Select a Type"
                style={{ margin: "5px 0" }}
                className={cx({
                  "p-invalid": item?.contractTypeId
                    ? errors.contractTypeId
                    : errors.projectTypeId,
                })}
                onChange={(e) => {
                  if (item?.contractTypeId) {
                    setValue("contractTypeId", e.value);
                  } else {
                    field.onChange(e.value);
                  }
                }}
              />
            )}
            rules={{
              // The dropdown writes to contractTypeId for contract-type items and to
              // projectTypeId otherwise - validate whichever field is actually in use.
              validate: (value) =>
                !!(item?.contractTypeId ? getValues("contractTypeId") : value) ||
                "Field is required",
            }}
            control={control}
          />
          {errors.projectTypeId && (
            <small className="p-error">{errors.projectTypeId.message}</small>
          )}
        </div>
      </If>
      <div className={classes.field}>
        <LabelValue label="Name" isRequired>
          <If condition={isScopeOfWorkItem}>
            <InputTextarea
              id="name"
              autoFocus
              className={cx({ "p-invalid": errors.name })}
              {...register("name", {
                required: "Field is required",
              })}
              style={{ margin: "5px 0" }}
              autoResize
            />
            <InputText
              id="name"
              autoFocus
              className={cx({ "p-invalid": errors.name })}
              {...register("name", {
                required: "Field is required",
              })}
              style={{ margin: "5px 0" }}
            />
          </If>
          {errors.name && (
            <small className="p-error">{errors.name.message}</small>
          )}
        </LabelValue>
      </div>
      {showAirslateTemplate && (
        <div className={cx(classes.field, classes.fileUpload)}>
          <LabelValue label="Template" />
          <div>
            <input
              type="file"
              accept="application/pdf,application/vnd.ms-excel"
              {...register("file")}
            />
            {/* @ts-ignore */}
            <span>{file ? file?.[0]?.name : "No file choosen"}</span>
          </div>
        </div>
      )}
      <If condition={!!showSowTemplate && !isScopeOfWorkItem}>
        <div className={cx(classes.field, classes.fileUpload)}>
          <LabelValue
            label={
              <>
                Scope of work template
                <Tooltip
                  target=".sow-template-info"
                  className={classes.sowTooltip}
                />
                <i
                  className="pi pi-info-circle sow-template-info"
                  style={{ marginLeft: "6px", cursor: "help" }}
                  data-pr-position="top"
                  data-pr-tooltip="An Excel file listing the full task list and area descriptions for this contract type. It can be downloaded while creating a contract and attached to it, and flows through to the job. If no template is attached, the scope-of-work items added through the app are used."
                />
              </>
            }
          />
          <div>
            {item?.hasSowTemplate && !sowTemplateFile ? (
              <small className={classes.fieldHint}>Current: template on file</small>
            ) : null}
            <input
              type="file"
              accept=".xls,.xlsx"
              onChange={(e) => setSowTemplateFile(e.target.files?.[0] ?? null)}
            />
            <span>
              {sowTemplateFile ? sowTemplateFile.name : "No file choosen"}
            </span>
          </div>
        </div>
      </If>
    </Dialog>
  );
};
